<template>
  <div>
    <!-- Blocks all interaction and shows a wait cursor while a request is in flight. -->
    <div v-if="busy" class="upload-busy-overlay"></div>

    <vx-card ref="listCard" :title="$t('Drilling.Upload.Title')" class="mb-3" title-color="black" :loading="busy" :loading-text="$t('Loading')">

      <!-- ── File selection (before validation) ───────────────────────── -->
      <div v-if="!isFileUploaded">
        <vs-row vs-type="flex" vs-justify="space-between" class="mb-4">
          <vs-col vs-w="12" vs-type="flex" vs-justify="flex-end" vs-align="center">
            <vs-button
              class="mr-3"
              type="border"
              :color="files.length > 0 ? 'danger' : 'dark'"
              :disabled="files.length === 0 || busy"
              @click="reset">
              <feather-icon icon="RotateCcwIcon" svgClasses="h-4 w-4 align-middle" />
              {{ $t('Drilling.Upload.Reset') }}
            </vs-button>

            <input
              type="file"
              class="inputfile"
              ref="fileInput"
              id="fileInput"
              @change="handleFileChange"
              accept=".xlsx,.xlsm,.csv"
              :disabled="busy"
              multiple />
            <label for="fileInput" class="mr-3" :class="{ 'inputfile-disabled': busy }">
              <feather-icon icon="FolderIcon" svgClasses="h-4 w-4 align-middle" />
              {{ browseFileLabel }}
            </label>

            <vs-button
              color="primary"
              icon-pack="feather"
              icon="icon-upload"
              :disabled="files.length === 0 || busy"
              @click="upload">
              {{ $t('Drilling.Upload.Upload') }}{{ files.length > 0 ? ` (${files.length})` : '' }}
            </vs-button>
          </vs-col>
        </vs-row>

        <vs-row class="mb-4">
          <vs-col vs-lg="4" vs-sm="6" vs-xs="12">
            <form-label class="vs-select--label input-select-label-primary--active">{{ $t('Drilling.Upload.Template') }}</form-label>
            <v-select
              v-model="template"
              :options="templateOptions"
              :multiple="false"
              :clearable="false"
              :reduce="_ => _.id"
              :disabled="busy"
              label="name"
              class="w-full" />
          </vs-col>
        </vs-row>

        <table v-if="files.length > 0" class="table table-bordered table-striped" width="100%">
          <thead>
            <tr>
              <th>{{ $t('Drilling.Upload.Filename') }}</th>
              <th width="120px"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(file, index) in files" :key="`${file.name}_${index}`">
              <td>{{ file.name }}</td>
              <td>
                <feather-icon
                  icon="TrashIcon"
                  svgClasses="w-5 h-5 hover:text-danger stroke-current"
                  class="cursor-pointer"
                  :title="$t('Drilling.Upload.Remove')"
                  @click.stop="removeFile(index)" />
                {{ $t('Drilling.Upload.Remove') }}
              </td>
            </tr>
          </tbody>
        </table>

        <div class="mt-3">{{ $t('Drilling.Upload.FilesSelected', { count: files.length }) }}</div>
        <div v-if="fileExtensionValidationMessage" class="text-danger mt-1">{{ fileExtensionValidationMessage }}</div>
      </div>

      <!-- ── Validation results (after upload) ─────────────────────────── -->
      <div v-if="isFileUploaded && uploadResults.files">
        <vs-row vs-type="flex" vs-justify="space-between" class="mb-2">
          <vs-col vs-w="8">
            <h4>{{ $t('Drilling.Upload.ValidationResults') }}</h4>
          </vs-col>
          <vs-col vs-w="4" vs-type="flex" vs-justify="flex-end" vs-align="center">
            <vs-button class="mr-3" type="border" color="danger" :disabled="busy" @click="reset">
              <feather-icon icon="RotateCcwIcon" svgClasses="h-4 w-4 align-middle" />
              {{ $t('Drilling.Upload.Reset') }}
            </vs-button>
            <vs-button color="primary" :disabled="busy" @click="ingest">
              <feather-icon icon="CheckCircleIcon" svgClasses="h-4 w-4 align-middle" />
              {{ $t('Drilling.Upload.Ingest') }}
            </vs-button>
          </vs-col>
        </vs-row>

        <div class="mb-4">
          {{ $t('Drilling.Upload.ValidationResultsDescription', { total: uploadResults.files.length, passed: passedCount, failed: failedCount }) }}
        </div>

        <table class="table table-bordered" width="100%">
          <thead>
            <tr>
              <th>{{ $t('Drilling.Upload.Filename') }}</th>
              <th width="160px">{{ $t('Drilling.Upload.StructureStatus') }}</th>
              <th width="160px">{{ $t('Drilling.Upload.ContentStatus') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="file in uploadResults.files">
              <!-- Main row -->
              <tr
                :key="file.filePath"
                :class="file.success ? '' : 'row-warning'"
                class="cursor-pointer"
                :title="$t('Drilling.Upload.ClickToShowDetails')"
                @click="toggleExpanded(file)">
                <td>
                  <feather-icon
                    :icon="file._expanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                    svgClasses="h-4 w-4 align-middle"
                    class="mr-2 text-grey" />
                  <span class="font-medium">{{ fileName(file) }}</span>
                </td>
                <td>
                  <span class="status-pill" :class="isStructureValid(file) ? 'pill-success' : 'pill-danger'">
                    <feather-icon :icon="isStructureValid(file) ? 'CheckIcon' : 'XIcon'" svgClasses="h-3 w-3 align-middle" />
                    {{ isStructureValid(file) ? $t('Drilling.Upload.Valid') : $t('Drilling.Upload.Invalid') }}
                  </span>
                </td>
                <td>
                  <span class="status-pill" :class="isContentValid(file) ? 'pill-success' : 'pill-danger'">
                    <feather-icon :icon="isContentValid(file) ? 'CheckIcon' : 'XIcon'" svgClasses="h-3 w-3 align-middle" />
                    {{ isContentValid(file) ? $t('Drilling.Upload.Valid') : $t('Drilling.Upload.Invalid') }}
                  </span>
                </td>
              </tr>

              <!-- Expandable details -->
              <tr v-if="file._expanded" :key="`${file.filePath}_details`">
                <td colspan="3" class="details-cell">
                  <!-- Structure validations (0-check pipelines) -->
                  <div v-if="structureValidations(file).length > 0" class="mb-3">
                    <div class="section-label">{{ $t('Drilling.Upload.StructureValidations') }}</div>
                    <span
                      v-for="validation in structureValidations(file)"
                      :key="validation.pipeline"
                      class="status-pill mr-2"
                      :class="validation.success ? 'pill-success' : 'pill-danger'">
                      <feather-icon :icon="validation.success ? 'CheckIcon' : 'XIcon'" svgClasses="h-3 w-3 align-middle" />
                      {{ pipelineName(validation.pipeline) }}
                    </span>
                  </div>

                  <!-- Content validations -->
                  <div v-if="contentValidations(file).length > 0">
                    <div class="section-label">{{ $t('Drilling.Upload.ContentValidations') }}</div>
                    <div v-for="validation in contentValidations(file)" :key="validation.pipeline" class="mb-4">
                      <div class="mb-2">
                        <span class="font-medium mr-2">{{ pipelineName(validation.pipeline) }}</span>
                        <span class="status-pill" :class="validation.success ? 'pill-success' : 'pill-danger'">
                          {{ validation.passCount }}/{{ validation.totalChecks }}
                        </span>
                        <span v-if="validation.message" class="text-grey ml-2">{{ validation.message }}</span>
                      </div>
                      <table v-if="validation.results && validation.results.length > 0" class="table table-unbordered" width="100%">
                        <thead>
                          <tr>
                            <th>{{ $t('Drilling.Upload.ValidationName') }}</th>
                            <th width="80px" class="text-center">{{ $t('Success') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="result in validation.results" :key="result.definitionName">
                            <td>{{ result.definitionName }}</td>
                            <td class="text-center">
                              <feather-icon
                                :icon="result.hasFailures ? 'XIcon' : 'CheckIcon'"
                                :svgClasses="result.hasFailures ? 'h-4 w-4 text-danger' : 'h-4 w-4 text-success'" />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <div v-if="!ingestCompleted" class="mt-3">
          <label v-if="uploadResults.files.every(f => f.success)">{{ $t('Drilling.Upload.ValidationResultsCompleteSuccess') }}</label>
          <label v-else-if="uploadResults.files.some(f => f.success)">{{ $t('Drilling.Upload.ValidationResultsPartialSuccess') }}</label>
        </div>
      </div>

      <!-- ── Outcome alerts ────────────────────────────────────────────── -->
      <div v-if="ingestCompleted" class="alert-success mt-3">
        <feather-icon icon="CheckCircleIcon" svgClasses="h-5 w-5 align-middle" class="mr-2" />
        <strong>{{ $t('Success') }}!</strong> {{ $t('Drilling.Upload.ImportationSuccessful') }}
      </div>
      <div v-if="ingestError" class="alert-danger mt-3">
        <feather-icon icon="AlertCircleIcon" svgClasses="h-5 w-5 align-middle" class="mr-2" />
        <strong>{{ $t('Error') }}!</strong> {{ $t('Drilling.Upload.IngestError') }}
      </div>
    </vx-card>
  </div>
</template>

<script>
import cqrsApiService from "@/app/shared/services/cqrsApiService.js";
import {
  FormLabel
} from "../../shared/components";
import vSelect from 'vue-select';
import FeatherIcon  from '@/components/FeatherIcon.vue'

const ALLOWED_EXTENSIONS = ['xlsx', 'xlsm', 'csv'];

// Mirrors the UploadTemplate enum (Vue 3 types.ts / AEM.Drilling.Business).
const UPLOAD_TEMPLATE = {
  AEMTemplate: 0,
  Coreplan: 1
};

export default {
  props: {
    // Backend file-processing controller route. Mirrors the Vue 3 GenericUpload
    // `serviceRoute` prop (DrillingPages.ts -> "drilling/stagingDailyReports").
    serviceRoute: {
      type: String,
      default: "Drilling/StagingDailyReports"
    }
  },
  components: {
    FormLabel,
    vSelect,
    FeatherIcon
  },
  data() {
    return {
      files: [],
      template: UPLOAD_TEMPLATE.AEMTemplate,
      uploadResults: {},
      isFileUploaded: false,
      uploadInProgress: false,
      ingestInProgress: false,
      ingestCompleted: false,
      ingestError: false,
      fileExtensionValidationMessage: ''
    };
  },
  computed: {
    busy() {
      return this.uploadInProgress || this.ingestInProgress;
    },
    templateOptions() {
      return [
        { id: UPLOAD_TEMPLATE.AEMTemplate, name: this.$t('Drilling.Upload.Templates.AEMTemplate') },
        { id: UPLOAD_TEMPLATE.Coreplan, name: this.$t('Drilling.Upload.Templates.Coreplan') }
      ];
    },
    browseFileLabel() {
      if (this.files.length === 0) return this.$t('Drilling.Upload.BrowseFiles');
      return this.$t('Drilling.Upload.AddMoreFiles');
    },
    passedCount() {
      return this.uploadResults.files.filter(f => f.validations.every(v => v.success)).length;
    },
    failedCount() {
      return this.uploadResults.files.filter(f => f.validations.some(v => !v.success)).length;
    }
  },
  methods: {
    handleFileChange() {
      const selected = Array.from(this.$refs.fileInput.files);
      selected.forEach(newFile => {
        const isDuplicate = this.files.some(f => f.name === newFile.name && f.size === newFile.size);
        if (!isDuplicate) {
          this.files.push(newFile);
        }
      });
      this.fileExtensionValidationMessage = '';
      // Reset the input so the same file can be re-selected after removal.
      this.$refs.fileInput.value = '';
    },
    removeFile(index) {
      this.files.splice(index, 1);
    },
    validateFiles() {
      const invalid = this.files.find(f => !ALLOWED_EXTENSIONS.includes(f.name.split('.').pop().toLowerCase()));
      if (invalid) {
        this.fileExtensionValidationMessage = this.$t('Drilling.Upload.BadFileExtension');
        return false;
      }
      this.fileExtensionValidationMessage = '';
      return true;
    },
    async toBase64(file) {
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return btoa(binary);
    },
    async upload() {
      if (this.files.length === 0 || !this.validateFiles()) {
        return;
      }

      try {
        document.body.style.cursor = 'wait';
        this.uploadInProgress = true;
        this.uploadResults = {};
        this.ingestCompleted = false;
        this.ingestError = false;

        const fileDetails = await Promise.all(this.files.map(async file => ({
          filename: file.name,
          mimeType: file.type,
          base64FileContent: await this.toBase64(file)
        })));

        let response;
        if (fileDetails.length === 1) {
          response = await cqrsApiService.Post(`${this.serviceRoute}/upload`, { file: fileDetails[0], template: this.template }, this);
        } else {
          response = await cqrsApiService.Post(`${this.serviceRoute}/uploadBatch`, { files: fileDetails, template: this.template }, this);
        }

        const model = response.data.model || { files: [] };
        // Tag each file with a local-only expanded flag for the detail toggle.
        this.uploadResults = { files: (model.files || []).map(f => ({ ...f, _expanded: false })) };
        this.isFileUploaded = true;
      } catch (e) {
        console.error(e);
      } finally {
        document.body.style.cursor = 'default';
        this.uploadInProgress = false;
      }
    },
    async ingest() {
      const uploadIds = [...new Set(this.uploadResults.files.map(f => f.asset && f.asset.uploadId).filter(Boolean))];
      if (uploadIds.length === 0) {
        this.$vs.notify({ color: "danger", title: this.$t('Error'), text: this.$t('Drilling.Upload.NoFilesToIngest') });
        return;
      }

      try {
        document.body.style.cursor = 'wait';
        this.ingestInProgress = true;
        this.ingestError = false;
        await Promise.all(uploadIds.map(uploadId => cqrsApiService.Post(`${this.serviceRoute}/ingest`, { uploadId, template: this.template }, this)));
        this.ingestCompleted = true;
        this.$vs.notify({ color: "success", title: this.$t('Success'), text: this.$t('Drilling.Upload.ImportationSuccessful') });
        this.reset();
        this.$router.back();
      } catch (e) {
        console.error(e);
        this.ingestError = true;
        this.$vs.notify({ color: "danger", title: this.$t('Error'), text: this.$t('Drilling.Upload.IngestError') });
      } finally {
        document.body.style.cursor = 'default';
        this.ingestInProgress = false;
      }
    },
    reset() {
      this.files = [];
      this.uploadResults = {};
      this.isFileUploaded = false;
      this.ingestCompleted = false;
      this.ingestError = false;
      this.fileExtensionValidationMessage = '';
    },
    toggleExpanded(file) {
      file._expanded = !file._expanded;
    },
    fileName(file) {
      return file.filePath ? file.filePath.split('/').pop() : '';
    },
    pipelineName(pipeline) {
      return pipeline ? pipeline.split('/').pop() : '';
    },
    isStructureValid(file) {
      return file.validations.filter(v => v.totalChecks === 0).every(v => v.success);
    },
    isContentValid(file) {
      return file.validations.filter(v => v.totalChecks > 0).every(v => v.success);
    },
    structureValidations(file) {
      return file.validations.filter(v => v.totalChecks === 0);
    },
    contentValidations(file) {
      return file.validations.filter(v => v.totalChecks > 0);
    }
  }
};
</script>

<style scoped>
.inputfile {
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
}
.inputfile + label {
  padding: 4px 10px;
  color: white;
  background-color: #009cde;
  display: inline-block;
  border: 1px solid #009cde;
  border-radius: 5px;
  cursor: pointer;
}
.inputfile:focus + label,
.inputfile + label:hover {
  background-color: #26abe3;
  border: 1px solid #1aa6e1;
}
.inputfile + label.inputfile-disabled {
  background-color: #b5b5b5;
  border-color: #b5b5b5;
  cursor: wait;
  pointer-events: none;
}
/* Full-viewport shield: captures every click and forces the wait cursor. */
.upload-busy-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  cursor: wait;
  background-color: rgba(255, 255, 255, 0.35);
}
.status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 1rem;
  color: #ffffff;
  font-size: 0.85rem;
  white-space: nowrap;
}
.pill-success {
  background-color: #28c76f;
}
.pill-danger {
  background-color: #ea5455;
}
.row-warning {
  background-color: #fff3cd;
}
.details-cell {
  background-color: #f8f8f8;
  padding: 1rem;
}
.section-label {
  text-transform: uppercase;
  font-weight: 600;
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
}
.table-unbordered {
  border-spacing: unset;
}
.alert-success {
  padding: 0.75rem 1rem;
  border-radius: 5px;
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}
.alert-danger {
  padding: 0.75rem 1rem;
  border-radius: 5px;
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}
</style>
